/**
 * Shared reading of the Materials Testing table.
 *
 * The table's rows are stored as JSON in the entries of a single field, and three layers need to
 * ask the same questions of them: the workflow section, the report output builder, and the action
 * items builder.  They live here so the rules can't drift apart between what the workflow shows and
 * what the report prints.
 */
import type { SectionFieldCollection, SectionFieldDTO } from '../magic-report.type';

export const MATERIAL_TESTING_TESTS_FIELD = 'MaterialTestingTests';

/** One row of the Materials Testing table, as stored. Fields are optional — rows are parsed from free-form JSON. */
export interface MaterialTestRow {
  testName?: string;
  everReceived?: string;
  anyCurrent?: string;
  failedTests?: string;
}

/** Parses the stored test rows, skipping any entry that isn't valid JSON. */
export const parseMaterialTestRows = (data: SectionFieldCollection | undefined): MaterialTestRow[] => {
  const testsField = data?.fields?.find((f: SectionFieldDTO) => f.fieldName === MATERIAL_TESTING_TESTS_FIELD);
  return (testsField?.entries || [])
    .map(entry => {
      try {
        return JSON.parse(entry.value || '') as MaterialTestRow;
      } catch {
        return null;
      }
    })
    .filter((row): row is MaterialTestRow => row != null);
};

/**
 * No testing reports have ever been received.  We then have no way of knowing who is doing the
 * testing, so the provider comment is hidden in the workflow and left out of the output.
 *
 * An empty table is deliberately not "never received" — with no rows there is nothing to assert.
 */
export const allTestsNeverReceived = (rows: MaterialTestRow[]): boolean => rows.length > 0 && rows.every(row => row.everReceived === 'No');

/** Nothing ever received and nothing this period — the state behind the retainage-hold recommendation. */
export const allTestsNeverReceivedAndNoneCurrent = (rows: MaterialTestRow[]): boolean =>
  rows.length > 0 && rows.every(row => row.everReceived === 'No' && row.anyCurrent === 'No');
